// src/lib/merge-by-id.js
// 2026-05-29 EXTRACTED from src/App.jsx (C1 refactor — WS/poll merge engine) as a
// behavior-preserving MOVE (function body byte-identical, only uniformly dedented
// from its old position inside AppInner and given an `export`).
//
// What this is:
//   mergeByID(localArr, serverArr, tableName, mode) is THE merge engine that every
//   data sync passes through — WebSocket broadcasts, polling refetch, and the
//   apiSave HTTP-409 conflict path (via mergeByIDGlobal in src/lib/api-client.js).
//   GOLDEN RULE it enforces: NEVER replace local state with server data — ALWAYS
//   merge by record id so local-only (offline / just-created) records and other
//   users' server records both survive.
//
// Why extract:
//   It was a function nested inside AppInner (~6741-LOC App.jsx monolith) but it
//   references NO AppInner closure state and NO React setters — only its arguments
//   plus already-extracted module-level singletons (lastKnownCounts from
//   network-state.js; deletedIDs / persistDeletedIDs / tombstonedIDs from
//   tombstones.js). So it is a NEAR-PURE function and moves out cleanly, the same
//   precedent as src/lib/tombstones.js and src/lib/connection-state.js. AppInner
//   still registers it for the global conflict path via setMergeByIDGlobal(mergeByID).
//
// Modes actually used in this codebase: 'sync' (WS / poll / 409 conflict — server is
//   authority) and the default/'init' path (initial load — push local offline edits
//   up). The function only branches on `mode === 'sync'`; any other value (or none)
//   takes the init branch.
//
import { lastKnownCounts } from './network-state.js';
import { deletedIDs, persistDeletedIDs, tombstonedIDs } from './tombstones.js';
import { dispatchToast } from './toast-event.js';
import { isStaleAtomicSnapshot } from './finance-page-merge.js';
import { serverNow } from './server-clock.js';

function dcAsOfDate(arr) {
  if (!Array.isArray(arr)) return null;
  const meta = arr.find((r) => r && r.id === 'dc-meta');
  const day = meta && meta.asOfDate;
  return typeof day === 'string' && day ? day : null;
}

// Один предикат смены дня для mergeByID, открытия Daily Calcs и ручного Refresh.
// Серверная dc-meta.asOfDate есть, а локальной нет или она ДРУГАЯ → серверный массив
// (мету ведёт только сервер: будущая/битая локальная дата — тоже чужой день, жюри GPT r5)
// (строка без id остаётся; id из deleted/persist/tombstone снимается). Иначе null.
export function takeServerDailyCalcsIfDayAhead(localArr, serverArr, tableName) {
  if (tableName !== 'daily-calcs-data' || !Array.isArray(serverArr)) return null;
  const serverDay = dcAsOfDate(serverArr);
  const localDay = dcAsOfDate(localArr);
  if (!serverDay || localDay === serverDay) return null;
  const localDeletedSet = deletedIDs[tableName] || new Set();
  const persistDeleted = persistDeletedIDs[tableName] || new Set();
  const tombstoned = tombstonedIDs[tableName] || new Set();
  return serverArr.filter((r) => {
    if (!r || typeof r !== 'object') return false;
    if (!r.id) return true;
    return !localDeletedSet.has(r.id) && !persistDeleted.has(r.id) && !tombstoned.has(r.id);
  });
}

export function mergeByID(localArr, serverArr, tableName, mode, onDropStale) {
  // mode: 'init' = initial load (merge local into server), 'sync' = poll/WS (server is authority)
  // Server returned null = fetch failed, keep local
  if (serverArr === null || serverArr === undefined) return Array.isArray(localArr) ? localArr : [];
  // v32.16.25 FIX (Sentry NODE-EXPRESS-H "t.forEach is not a function", 2026-06-08): this
  // engine only checked null/undefined, then ASSUMED serverArr was an array. A truthy NON-array
  // server payload (an error object, a malformed/HTML body, or a transient non-array seen during
  // a version-mismatch reload — this engine runs in the WS/poll sync path) skipped every
  // `.length`-based guard below (undefined !== 0) and reached `serverArr.forEach` → threw an
  // unhandled promise rejection on mobile. Treat a non-array payload like a failed fetch: keep
  // local, never replace good local data with garbage.
  if (!Array.isArray(serverArr)) {
    console.warn(`⚠️ MERGE [${tableName}]: server payload is ${typeof serverArr}, not an array — keeping local`);
    return Array.isArray(localArr) ? localArr : [];
  }
  // Defensive twin: corrupt localStorage could make localArr a non-array → it would throw at
  // `localArr.forEach` below. Normalize to [] (handled as the empty-local path on line ~70).
  if (localArr != null && !Array.isArray(localArr)) localArr = [];
  // 2026-09-23: новый серверный день Daily Calcs — таблица целиком с сервера, любой mode.
  // LWW ниже оставляет строку с более свежим updatedAt (набор в полночь / спешащие часы)
  // со вчерашними Yesterday/Today (balanceNoCrg / balanceWithCrg), а новую dc-meta уже берёт.
  // Следующий сейв идёт с новой метой, guard пропускает, старый Yesterday возвращается на сервер.
  // То же правило — takeServerDailyCalcsIfDayAhead (ещё открытие страницы и ручной Refresh).
  const serverDayTable = takeServerDailyCalcsIfDayAhead(localArr, serverArr, tableName);
  if (serverDayTable) return serverDayTable;
  if (
    (tableName === 'payments' || tableName === 'customer-payments') &&
    Array.isArray(localArr)
  ) {
    // runtime clock: _pendingStartedAt is raw Date.now(); do not pass serverNow().
    localArr = localArr.filter((record) => !isStaleAtomicSnapshot(record));
  }
  // v9.09 FIX: Don't blindly trust empty server response if local has data
  if (serverArr.length === 0 && Array.isArray(localArr) && localArr.length > 0) {
    console.warn(`⚠️ Server returned 0 records for ${tableName} but local has ${localArr.length}. Keeping local data and pushing to server.`);
    return localArr;
  }
  // v9.16: If server returns empty AND local is empty, check lastKnownCounts
  // This catches the case where deploy wiped both server data and localStorage
  if (serverArr.length === 0 && (!localArr || localArr.length === 0)) {
    const lastKnown = lastKnownCounts[tableName] || 0;
    if (lastKnown > 5) {
      console.error(`🔴 DATA LOSS [${tableName}]: server=0, local=0, but last known count was ${lastKnown}. Data may have been wiped during deploy.`);
      dispatchToast(`⚠️ ${tableName} appears empty — may need server backup restore`, 'warning');
    }
    return [];
  }
  if (serverArr.length === 0) return [];
  if (!localArr || localArr.length === 0) return serverArr;

  // FIX: Users + wallets are server-authoritative — NEVER push local versions to server on init.
  // Local users can have Joy or stale permissions; local wallets can have v20.2 data.
  // Both cause visible bugs every deploy when client pushes stale localStorage.
  // 2026-06-02: selling-offers joins them — it's a CURATED list (Alex picks offers online),
  // so the server is its sole source of truth. Without this, the init-mode local-record
  // re-add below (line ~107) resurrects a deleted offer that still sits in stale localStorage
  // (its tombstone was already cleared by apiSave's getAndClearDeletes) → "deleted Selling
  // offers come back after reload". Server-authoritative breaks that self-perpetuating cycle.
  if (tableName === 'users' || tableName === 'wallets' || tableName === 'selling-offers') return serverArr;

  // FIX: Respect locally deleted records — don't resurrect them from server data
  // v9.15: Also check persistDeletedIDs which survives clear cycles
  // v10.06: Also check tombstonedIDs — records permanently deleted on server
  const localDeletedSet = deletedIDs[tableName] || new Set();
  const persistDeleted = persistDeletedIDs[tableName] || new Set();
  const tombstoned = tombstonedIDs[tableName] || new Set();

  const merged = new Map();
  serverArr.forEach(r => {
    if (r && r.id && !localDeletedSet.has(r.id) && !persistDeleted.has(r.id) && !tombstoned.has(r.id)) merged.set(r.id, r);
  });
  let added = 0, updated = 0;
  localArr.forEach(r => {
    if (!r || !r.id) return;
    // v10.06: Skip tombstoned records from local data
    if (tombstoned.has(r.id) || localDeletedSet.has(r.id) || persistDeleted.has(r.id)) return;
    const srv = merged.get(r.id);
    if (!srv) {
      // Local record not on server
      // 2026-08-31 (FR2, fix-round — жюри A CRITICAL-2): threshold hoisted OUT of the two
      // branches below — it used to be 24h here (sync) vs 7d in the init branch (P4 fixed
      // ONLY init on 2026-08-31; this sync branch is a DIFFERENT, untouched code path of
      // the SAME function, triggered by every WS-broadcast/15s-poll tick, i.e. far more
      // often than boot). The 2026-05-16 comment that used to sit on the sync branch
      // literally named cross-path threshold drift a "silent footgun" — this WAS that
      // footgun, recreated by P4 fixing only one of the two branches.
      const age = serverNow() - (r.createdAt || r.updatedAt || 0); // record clock
      const MAX_LOCAL_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days — shared by sync AND init below
      if (mode === 'sync') {
        // During sync: server is authority — DON'T add old local records.
        // 2026-08-31 (FR2-GUILLOTINE, тихая потеря правок): was 24h with NO rescue at all
        // (onDropStale accepted a 5th param since P4, but this branch never called it) —
        // an offline payments/customer-payments record older than 24h (but well under the
        // 7d init-mode allowance) vanished from React state AND localStorage on the very
        // first unrelated WS broadcast or poll tick after it turned 24h old. Aligned to the
        // same MAX_LOCAL_AGE and rescue contract as the init branch below.
        if (age < MAX_LOCAL_AGE) { merged.set(r.id, r); added++; }
        else {
          console.warn(`⏭ MERGE [${tableName}] sync: dropped stale local-only ${r.id} (age=${Math.round(age/86400000)}d)`);
          if (typeof onDropStale === 'function') {
            try { onDropStale(r, age); } catch { /* rescue must never break the merge */ }
          }
        }
      } else {
        // During init: push local records to server (offline edits)
        // FIX: added 7-day age cap — prevents old March records in localStorage from
        // resurrecting on server when a new session starts (was unlimited → old records came back)
        // 2026-08-31 (P4-GUILLOTINE, тихая потеря правок, решение владельца): payments/
        // customer-payments раньше резали здесь по 24ч (было "payments only 24h, junk rows
        // on deploy") — реалистичный офлайн (выходные, больничный) молча терял правку.
        // Порог выровнен на общий MAX_LOCAL_AGE (7d, как у всех остальных таблиц):
        // воскрешение удалённой записи блокируют tombstones на сервере, клиентская
        // гильотина для этого не нужна. Записи, всё же перешагнувшие 7d, больше не
        // исчезают молча — onDropStale (опционально; App.jsx boot-merge зовёт им
        // persistDroppedSave, api-client.js) кладёт их в тот же тихий склад
        // blitz_dropped_saves, что и flushPendingSaves после исчерпанных retry.
        if (age < MAX_LOCAL_AGE) { merged.set(r.id, r); added++; }
        else {
          console.warn(`⏭ MERGE [${tableName}] init: skipping stale local record ${r.id} (age=${Math.round(age/86400000)}d)`);
          if (typeof onDropStale === 'function') {
            try { onDropStale(r, age); } catch { /* rescue must never break the merge */ }
          }
        }
      }
    }
    else {
      // v31.79: server-authoritative для crg-deals + daily-cap в sync mode.
      // ─────────────────────────────────────────────────────────────────────
      // ROOT CAUSE: strict `lt > st` silently dropped server's updates когда
      // local.updatedAt был equal/newer (race condition при rapid edits, или
      // clock skew между browser и server). Эти таблицы — source of truth
      // на сервере (live CRG operations + bot auto-sync). User reported
      // B/C/D systemic — все обновления invisible до F5 reload.
      //
      // FIX: для crg-deals + daily-cap не override merged.get(r.id) — server's
      // version (already set on line 11896) остаётся. Recent local-only creates
      // preserved через mode='sync' branch выше (line 11907-11911, 30-min window
      // для unpushed debounced saves). Init mode (offline edit migration) — old
      // logic preserved через else.
      //
      // Trade-off: если user активно типит в input на crg-deal в момент
      // прихода WS broadcast от другого юзера — local in-progress edit
      // clobbered. Acceptable для CRG где edits = quick clicks (Started
      // toggle, manage_aff chip, cap typing). Memory style: prefer correctness
      // over edge-case (notification_silent_suppression_anti_pattern.md).
      if ((tableName === 'crg-deals' || tableName === 'daily-cap') && mode === 'sync') {
        // No-op: server's version (already in `merged` from line 11896) wins.
        // updated++ to count for the log.
        updated++;
      } else {
        const lt = r.updatedAt || r.lastModified || 0;
        const st = srv.updatedAt || srv.lastModified || 0;
        if (lt > st) { merged.set(r.id, r); updated++; }
      }
    }
  });
  const result = Array.from(merged.values());
  if (added > 0 || updated > 0) console.log(`\u{1F500} MERGE [${tableName}] (${mode || 'init'}): server=${serverArr.length} +local_new=${added} +local_updated=${updated} = ${result.length}`);
  return result;
}

// 2026-08-31 (P3-BOOTMERGE, тихая потеря правок): companion к mergeByID для App.jsx's
// boot-merge effect — mirrors finance-page-merge.js's financePageMergeDivergedFromServer
// (same "did the merge diverge from the server snapshot" question for the OTHER merge
// path). Was: App.jsx pushed to the server only `if (merged.length > srv.length)` — a
// local EDIT of an EXISTING row (same count, newer updatedAt) never satisfied that and
// was never pushed. Content comparison catches edits, not just additions.
//
// Also decides what to seed saveBaselines/lastSavedHash with: when nothing diverged, the
// merged snapshot IS the server snapshot (safe to treat as already-saved). When local won
// something, seeding with the LOCAL (merged) snapshot would make the debounced-save guard
// (App.jsx performSave, currentJson === saveBaselines) treat the not-yet-confirmed edit as
// already delivered — seeding with the server snapshot instead keeps that guard honest so
// a later debouncedSave (or a retry) still sees a real difference to send.
export function computeBootMergePush(merged, srv) {
  const mergedJson = JSON.stringify(merged);
  const srvJson = JSON.stringify(Array.isArray(srv) ? srv : []);
  const diverged = mergedJson !== srvJson;
  return { diverged, mergedJson, srvJson, baselineJson: diverged ? srvJson : mergedJson };
}
